import { isWorkflowOverviewGroup, isWorkflowOverviewItem } from '../predicates'
import type { WorkflowReviewStatusData } from '@app/shared/types/sf-data'
import type {
  PreparedWorkflowOverviewAccountEntry,
  PreparedWorkflowOverviewReconciliationText,
  WorkflowOverviewGroup,
  WorkflowOverviewItem,
  WorkflowOverviewItemType,
} from '@app/application/workflow-overview-navigation/types'
import { isNotEmptyTree } from '@app/utils/predicates'

import fuzzysort from 'fuzzysort'
import { defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

import {
  exposeTopSingleParent,
  groupReviewStatus,
  prepareSearchWorkflowItem,
} from './utils'
import { SfLog } from '@app/adapters/logger'
import { getOverview } from '@app/application/workflow-overview-navigation/api'
import TreeNode from '@app/utils/tree'

import { useWorkflowOverviewSearch } from './search'
import { useWorkflowReviewItemStatus } from '@app/application/workflows/screens/workflow-review/store/pinia/workflow-review-item-status'

export const useWorkflowOverview = defineStore('workflow-overview', () => {
  const reviewStatusStore = useWorkflowReviewItemStatus()
  const searchStore = useWorkflowOverviewSearch()

  const { reviewItemStatus } = storeToRefs(reviewStatusStore)
  const { searchTerm } = storeToRefs(searchStore)

  const overviewTree = ref<
    TreeNode<WorkflowOverviewGroup | WorkflowOverviewItem>
  >(
    TreeNode.of(
      {
        id: 1,
        name: 'root',
        type: 'group',
        children: [],
      },
      []
    )
  )

  const overviewArray = computed(() => {
    return overviewTree.value.unwrapDeep(false)
  })

  const overviewTreeData = computed(() => {
    if (hasAnyFilterApplied.value) {
      return filteredTreeData.value
    }

    return overviewArray.value
  })

  const expandedOverviewGroups = ref<Record<string, boolean>>({})
  const permanentlyExpandedGroups = ref<Record<string, boolean>>({})

  const allExpandedGroups = computed(() => {
    return {
      ...expandedOverviewGroups.value,
      ...permanentlyExpandedGroups.value,
    }
  })

  const hasAnyFilterApplied = computed(() => {
    return searchTerm.value !== ''
  })

  const isOverviewTreeEmpty = computed(() => {
    return overviewTree.value.children.length === 0
  })

  const itemAndGroupReviewsById = computed(() => {
    const accumulator = {} as Record<
      string,
      Pick<WorkflowReviewStatusData, 'review' | 'reviews'>
    >

    for (const treeItem of overviewTree.value.reverseIterator()) {
      const item = treeItem.unwrap()

      if (!item) {
        continue
      }

      if (item.type === 'group') {
        const reviewResult = groupReviewStatus(
          item,
          reviewItemStatus.value,
          accumulator
        )

        if (reviewResult.reviews.length > 0 || reviewResult.review) {
          accumulator[item.id] = reviewResult
        }
      } else {
        const key =
          item.type === 'reconciliation_text' ? item.id : item.accountEntryId

        if (item.type && key) {
          const itemStatus = reviewItemStatus.value[`${item.type}:${key}`]

          if (itemStatus?.reviews?.length > 0 || itemStatus?.review) {
            accumulator[key] = itemStatus
          }
        }
      }
    }

    return accumulator
  })

  const preparedSearchCollection = computed(() => {
    const collection = [] as Array<
      | PreparedWorkflowOverviewAccountEntry
      | PreparedWorkflowOverviewReconciliationText
    >

    if (overviewTree.value) {
      for (const item of overviewTree.value) {
        const rawItem = item.unwrap()

        if (!isWorkflowOverviewGroup(rawItem)) {
          const preparedItem = prepareSearchWorkflowItem(rawItem)

          if (preparedItem) {
            collection.push(preparedItem)
          }
        }
      }
    }

    return collection
  })

  const searchResults = computed(() => {
    const fuzzyResults = fuzzysort.go(
      searchTerm.value,
      preparedSearchCollection.value,
      {
        keys: ['preparedName', 'preparedNumber', 'preparedOriginalNumber'],
        threshold: -1000,
      }
    )

    return fuzzyResults.reduce((acc, result) => {
      const item = { ...result }
      acc[item.obj.id] = true

      return acc
    }, {} as Record<number, boolean>)
  })

  const filteredTree = computed(() => {
    const results = searchResults.value

    const filteredTree = overviewTree.value.filter((item) => {
      if (isWorkflowOverviewGroup(item)) {
        return true
      }

      return !!results[item.id]
    })

    return filteredTree
  })

  const filteredElementsById = computed(() => {
    const accumulator = {
      account_entry: {} as Record<number, boolean>,
      reconciliation_text: {} as Record<number, boolean>,
      filteredIndeces: [] as Record<number, number>,
      groups: {} as Record<number, boolean>,
    }

    if (
      !filteredTree.value ||
      !isNotEmptyTree(filteredTree.value) ||
      !searchTerm.value
    ) {
      return accumulator
    }

    let itemIndex = 0

    for (const item of filteredTree.value) {
      const unwrapedItem = item.unwrap()

      if (isWorkflowOverviewGroup(unwrapedItem)) {
        accumulator.groups[unwrapedItem.id] = true
      } else {
        if (unwrapedItem.type === 'reconciliation_text') {
          accumulator.reconciliation_text[unwrapedItem.id] = true
          accumulator.filteredIndeces[unwrapedItem.reviewIndex] = itemIndex++
        } else {
          if (unwrapedItem.accountEntryId) {
            accumulator.account_entry[unwrapedItem.accountEntryId] = true
            accumulator.filteredIndeces[unwrapedItem.reviewIndex] = itemIndex++
          }
        }
      }
    }

    return accumulator
  })

  const filteredGroupsById = computed(() => {
    return filteredElementsById.value.groups
  })

  const filteredReviewIndeces = computed(() => {
    return filteredElementsById.value.filteredIndeces
  })

  const filteredTreeData = computed(() => {
    return filteredTree.value && isNotEmptyTree(filteredTree.value)
      ? filteredTree.value.unwrapDeep(false)
      : []
  })

  function collapseOverviewTree(): void {
    expandedOverviewGroups.value = {}
    permanentlyExpandedGroups.value = Object.keys(
      permanentlyExpandedGroups.value
    ).reduce((acc, groupId) => {
      acc[groupId] = true
      return acc
    }, {} as Record<string, boolean>)
  }

  function expandHigherOrderGroups(): void {
    for (const treeItem of overviewTree.value) {
      const item = treeItem.unwrap()
      if (isWorkflowOverviewGroup(item)) {
        const treeItemHasGroupChildren = item.children.find((child) =>
          isWorkflowOverviewGroup(child)
        )
        if (treeItemHasGroupChildren) {
          expandOverviewGroup(item.id.toString())
        }
      }
    }
  }

  async function fetchOverview({
    filter = '',
    includeReviewStatus,
    isPulseUser,
    highlightedItemId,
    highlightedItemType,
    ledgerId,
    workflowId,
  }: {
    filter?: string
    isPulseUser?: boolean
    highlightedItemId?: number
    highlightedItemType?: WorkflowOverviewItemType
    includeReviewStatus: boolean
    ledgerId: number
    workflowId: number
  }): Promise<void> {
    try {
      const response = await getOverview({
        filter,
        includeReviewStatus,
        highlightedItemId,
        highlightedItemType,
        ledgerId,
        workflowId,
      })

      if (!response || response.length === 0) {
        return
      }

      const singleParent = exposeTopSingleParent(response)

      const root = TreeNode.of<WorkflowOverviewGroup | WorkflowOverviewItem>(
        {
          id: 1,
          name: 'root',
          type: 'group',
          children: [],
        },
        response
      )
        .map((value) => {
          if (isWorkflowOverviewGroup(value)) {
            return Object.freeze(value)
          } else if (isWorkflowOverviewItem(value)) {
            const effectivePath = isPulseUser ? value.proposalPath : value.path

            return Object.freeze({
              ...value,
              path: effectivePath,
            })
          }

          return value
        })
        .filter((item) => {
          if (isWorkflowOverviewGroup(item)) {
            return item.name === 'root' || item.children.length > 0
          } else {
            if (!item?.id || !item?.type) {
              return false
            }

            return true
          }
        })

      if (isNotEmptyTree(root)) {
        overviewTree.value = root
      }

      if (singleParent !== -1) {
        permanentlyExpandedGroups.value = {
          ...permanentlyExpandedGroups.value,
          [singleParent]: true,
        }
      }

      if (includeReviewStatus) {
        // We don't have the Workflow Review Status Data from the Review Screen so we need to extract the review status from the overview tree
        response.forEach(extractReviewData)
      }
    } catch (e) {
      SfLog.warn('Error fetching overview', {
        extra: {
          error: e,
        },
      })
    }
  }

  function extractReviewData(
    item: WorkflowOverviewItem | WorkflowOverviewGroup
  ): void {
    if (item.type === 'group') {
      item.children.forEach(extractReviewData)
    } else {
      if (item.workflowReviewStatusData && item.type) {
        reviewStatusStore.updateReviewItemStatus({
          ...item.workflowReviewStatusData,
          itemClass: item.type,
          parentId:
            item.type === 'account_entry'
              ? item.accountEntryId
                ? item.accountEntryId
                : item.id
              : item.id,
        })
      }
    }
  }

  function expandOverviewGroup(groupId: string): void {
    expandedOverviewGroups.value = {
      ...expandedOverviewGroups.value,
      [groupId]: true,
    }
  }

  function expandItemParents(
    itemId: number,
    type: WorkflowOverviewItemType | 'group'
  ): void {
    const item = overviewTree.value.find((i) => {
      if (!isWorkflowOverviewGroup(i)) {
        if (i.type === 'account_entry') {
          return i.accountEntryId === itemId && i.type === type
        } else {
          return i.id === itemId && i.type === type
        }
      }

      return i.id === itemId
    })

    if (!item || !item?.parent || !isNotEmptyTree(item)) {
      return
    }

    const parentId = item.parent.unwrap().id

    expandOverviewGroup(parentId.toString())
    expandItemParents(parentId, 'group')
  }

  function toggleOverviewGroup(groupId: string): void {
    const isPermanent =
      typeof permanentlyExpandedGroups.value[groupId] !== 'undefined'

    if (isPermanent) {
      permanentlyExpandedGroups.value = {
        ...permanentlyExpandedGroups.value,
        [groupId]: !permanentlyExpandedGroups.value[groupId],
      }
    } else {
      expandedOverviewGroups.value = {
        ...expandedOverviewGroups.value,
        [groupId]: !expandedOverviewGroups.value[groupId],
      }
    }
  }

  return {
    allExpandedGroups,
    collapseOverviewTree,
    fetchOverview,
    filteredElementsById,
    filteredGroupsById,
    filteredReviewIndeces,
    hasAnyFilterApplied,
    expandOverviewGroup,
    expandItemParents,
    isOverviewTreeEmpty,
    itemAndGroupReviewsById,
    overviewTreeData,
    toggleOverviewGroup,
    expandHigherOrderGroups,
  }
})
